import { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { FormState, FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getTermsUrl } from '@config/index';
import { IProductConfiguration } from '@vox/front-configurator';
import Modal from '@components/Modal/Modal';
import Input from '@components/Input/Input';
import Textarea from '@components/Textarea/Textarea';
import Select from '@components/Select/Select';
import Checkbox from '@components/Checkbox/Checkbox';
import Button from '@components/Button/Button';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import { IFrontConfiguratorQuoteRequest } from '@features/cart/typescript';
import { required, emailValid, phone, requiredConsent } from '@features/userPanel/mobx/stores/validators';
import { TCountry } from '@typescript/regional';
import { postal } from '@features/userPanel/mobx/stores/validators';
import NextSurveyApi from '@features/api/nextApiService/nextSurveyApi';
import alertsStore from '@context/global/mobx/alertStore';
import styles from './QuoteRequestModal.styles';
import useBodyOverflow from '@hooks/useBodyOverflow';
type PropsType = {
  isOpen: boolean;
  onClose: () => void;
  quoteRequestData: IFrontConfiguratorQuoteRequest | null;
  configuratorData: IProductConfiguration;
};
const QuoteRequestModal = ({
  isOpen,
  onClose,
  quoteRequestData,
  configuratorData
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    locale,
    defaultLocale
  } = useRouter();
  const regulationsHref = getTermsUrl(locale, defaultLocale);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [consentValue, setConsentValue] = useState(false);
  const [isUrlCopied, setIsUrlCopied] = useState(false);
  useBodyOverflow(isOpen, 'quoteRequestModal', true, true);
  const validatePostalCode = async (val: string | null) => {
    const postalCode: string | undefined = val?.trim();

    // --------- validate required ------------//
    let error = required()(postalCode);
    if (error) {
      return error;
    }

    // --------- validate format ------------//
    error = postal('PL' as TCountry)(postalCode);
    if (error) {
      return error;
    }
    return error;
  };

  // Form state
  const quoteForm = useMemo(() => new FormState({
    fullName: new FieldState<string>('').validators(required()).disableAutoValidation(),
    postalCode: new FieldState<string>('').validators(validatePostalCode).disableAutoValidation(),
    email: new FieldState<string>('').validators(required(), emailValid()).disableAutoValidation(),
    phoneCountryCode: new FieldState<string>('PL'),
    phoneNumber: new FieldState<string>('').validators(required(), phone('PL' as TCountry)).disableAutoValidation(),
    comment: new FieldState<string>('').validators().disableAutoValidation(),
    consent: new FieldState<boolean>(false).validators(requiredConsent()).enableAutoValidation()
  }), []);

  // Phone country codes options
  const phoneCountryCodes = [{
    name: '+48',
    value: 'PL'
  }];

  // Update product name when quoteRequestData changes
  useEffect(() => {
    // Dependency tracked for future use
  }, [quoteRequestData]);

  // Reset success state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Synchronize local consent state with FormState
  useEffect(() => {
    quoteForm.$.consent.onChange(consentValue);
  }, [consentValue, quoteForm]);
  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsUrlCopied(true);
    setTimeout(() => {
      setIsUrlCopied(false);
    }, 2000);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const validationResult = await quoteForm.validate();
      if (validationResult.hasError) {
        setIsSubmitting(false);
        return;
      }

      // Pobranie obrazu produktu z quoteRequestData
      const productColor = quoteRequestData?.name || '';

      // Przygotowanie numeru telefonu
      const countryCode = quoteForm.$.phoneCountryCode.value;
      const fullPhoneNumber = `${countryCode} ${quoteForm.$.phoneNumber.value}`;
      const selectedCode = quoteRequestData?.code;
      const fileMatchingSelectedFeature = selectedCode ? configuratorData.files.find(file => {
        const raw = file.features;
        if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
          return false;
        }
        return Object.values(raw).some(values => Array.isArray(values) && values.some(v => v === selectedCode));
      }) : undefined;
      const productImage = fileMatchingSelectedFeature?.url || configuratorData.files.find(file => file.code === 'PRODUCT_DEDICATED')?.url || configuratorData.files.find(file => file.code === 'PRODUCT_MAIN')?.url || '';

      // Wysłanie zapytania
      const response = await NextSurveyApi.submitQuoteRequest({
        fullName: quoteForm.$.fullName.value,
        postalCode: quoteForm.$.postalCode.value,
        email: quoteForm.$.email.value,
        phoneNumber: fullPhoneNumber,
        comment: quoteForm.$.comment.value || '',
        productName: configuratorData.name,
        productColor: productColor,
        productImage,
        productErp: configuratorData.configuration.uuid || undefined
      });
      if (response.error) {
        alertsStore.pushAlert({
          message: response.message ? t(response.message) : t('quoteRequest.error.submitFailed'),
          display: true
        });
        setIsSubmitting(false);
        return;
      }
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alertsStore.pushAlert({
        message: t('quoteRequest.error.submitFailed'),
        display: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Modal active={isOpen} closeFunc={handleClose} heading={t('quoteRequest.title')} closeOnOutsideClick fullScreen={true} addContentSx={styles.modalContainer(isSuccess)} addSx={styles.modalHeading} headingAddSx={styles.modalHeadingText}>
      <div sx={styles.modalContent}>
        {isSuccess ? (/* Success Screen */
      <>
            <div sx={styles.successContainer}>
              <div sx={styles.successIconContainer}>
                <div sx={styles.successIcon}>
                  <Icon icon="Check" size="64px" color="darkGrey700" />
                </div>
              </div>
              <div sx={styles.textContainer}>
                <Heading h={5} addSx={styles.successTitle}>
                  {t('quoteRequest.success.title')}
                </Heading>
                <p sx={styles.successMessage}>{t('quoteRequest.success.message')}</p>
              </div>
            </div>
            <div sx={styles.successFooter}>
              <Button variant="default" variantColor="dark" onClick={handleClose} addSx={styles.closeButton}>
                {t('quoteRequest.success.closeButton')}
              </Button>
            </div>
          </>) : (/* Form Screen */
      <>
            {/* Scrollable Content */}
            <div sx={styles.scrollableContent}>
              {/* Product Section */}
              <div sx={styles.section}>
                <Heading h={4} headingType={4} addSx={styles.sectionTitle}>
                  {t('quoteRequest.product')}
                </Heading>
                <div sx={styles.productContainer}>
                  <Input field={new FieldState<string>(configuratorData.name || '')} placeholder={''} disabled={true} addSx={styles.productNameInput} />
                  <button onClick={handleCopyUrl} sx={styles.copyUrlButton} aria-label="Kopiuj adres strony">
                    <Icon icon={isUrlCopied ? 'Check' : 'Copy'} size="24px" color="darkGrey700" />
                  </button>
                </div>
                <div sx={styles.colorTile}>
                  <span sx={styles.colorTileValue}>
                    {t('quoteRequest.productColor')}: {quoteRequestData?.name || ''}
                  </span>
                </div>
              </div>

              {/* Inquiry Details Section */}
              <div sx={styles.section}>
                <Heading h={4} headingType={4} addSx={styles.sectionTitle}>
                  {t('quoteRequest.inquiryDetails')}
                </Heading>
                <Input field={quoteForm.$.fullName} placeholder={t('quoteRequest.fullName')} addSx={styles.fullNameInput} />
                <Input field={quoteForm.$.postalCode} placeholder={t('quoteRequest.postalCode')} addSx={styles.postalCodeInput} />
                <Input field={quoteForm.$.email} placeholder={t('quoteRequest.email')} addSx={styles.emailInput} />
                <div sx={styles.phoneContainer}>
                  <Select value={quoteForm.$.phoneCountryCode.value} onChange={value => quoteForm.$.phoneCountryCode.onChange(value.toString())} options={phoneCountryCodes} addSx={styles.phoneCountryCodeSelect} disabled={true} />
                  <Input field={quoteForm.$.phoneNumber} placeholder={t('quoteRequest.phoneNumber')} addSx={styles.phoneNumberInput} />
                </div>
                <p sx={styles.requiredFieldsNote}>{t('quoteRequest.requiredFields')}</p>
              </div>

              {/* Inquiry Content Section */}
              <div sx={styles.section}>
                <Heading h={4} headingType={4} addSx={styles.sectionTitle}>
                  {t('quoteRequest.inquiryContent')}
                </Heading>
                <Textarea field={quoteForm.$.comment} placeholder={t('quoteRequest.comment')} maxLength={2500} bottomDescription={t('quoteRequest.commentPlaceholder')} />
              </div>
            </div>

            {/* Footer - Always Visible at Bottom */}
            <div sx={styles.footer}>
              <div sx={styles.consentContainer}>
                <Checkbox value={consentValue} onChange={setConsentValue} showError={!!quoteForm.$.consent.error} error={quoteForm.$.consent.error || ''} addSx={styles.checkboxContainer}>
                  <span sx={styles.consentText}>
                    {t('quoteRequest.consent')}{' '}
                    <a href={regulationsHref} target="_blank" rel="noopener noreferrer" sx={styles.regulationsLink}>
                      {t('quoteRequest.regulationsLink')}
                    </a>
                  </span>
                </Checkbox>
              </div>
              <Button variant="default" variantColor="brand" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting} addSx={styles.submitButton}>
                {t('quoteRequest.submitButton')}
              </Button>
            </div>
          </>)}
      </div>
    </Modal>;
};
export default observer(QuoteRequestModal);